<template>
  <div class="opener-specialists-section">
    <div class="container">
      <div class="row align-items-center">
        <div class="col-lg-6 mb-4 mb-lg-0">
          <div class="content-wrapper">
            <h2 class="section-title text-white mb-4">
              {{
                (serviceData &&
                  serviceData.serviceSpecialist &&
                  serviceData.serviceSpecialist.title) ||
                "Service Specialists In Your Area"
              }}
            </h2>
            <div class="section-description text-white">
              <p
                v-for="(paragraph, index) in (serviceData &&
                  serviceData.serviceSpecialist &&
                  serviceData.serviceSpecialist.description) ||
                []"
                :key="index"
                class="mb-4"
              >
                <span
                  v-if="
                    serviceData &&
                    serviceData.serviceSpecialist &&
                    serviceData.serviceSpecialist.phone &&
                    paragraph.includes(serviceData.serviceSpecialist.phone)
                  "
                >
                  {{ paragraph.split(serviceData.serviceSpecialist.phone)[0] }}
                  <a href="tel:5514080587">{{
                    serviceData.serviceSpecialist.phone
                  }}</a>
                  {{ paragraph.split(serviceData.serviceSpecialist.phone)[1] }}
                </span>
                <span v-else>{{ paragraph }}</span>
              </p>
            </div>
          </div>
        </div>
        <div class="col-lg-6">
          <div class="media-wrapper">
            <!-- Video support -->
            <video
              v-if="isVideo()"
              :src="getMediaPath()"
              :alt="getImageAlt()"
              :title="getImageTitle()"
              class="opener-media img-fluid rounded shadow-lg"
              autoplay
              muted
              loop
              playsinline
            >
              Your browser does not support the video tag.
            </video>
            <!-- Image fallback -->
            <img
              v-else
              :src="getMediaPath()"
              :alt="getImageAlt()"
              :title="getImageTitle()"
              class="opener-media img-fluid rounded shadow-lg"
            />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: "GarageDoorServiceSpecialist",
  props: {
    serviceData: {
      type: Object,
      default: () => ({}),
    },
    cityName: {
      type: String,
      default: "",
    },
  },
  methods: {
    isVideo() {
      const media =
        this.serviceData &&
        this.serviceData.serviceSpecialist &&
        (this.serviceData.serviceSpecialist.video ||
          this.serviceData.serviceSpecialist.image);

      if (!media) return false;

      // Check if video property exists
      if (this.serviceData.serviceSpecialist.video) return true;

      // Check file extension
      return /\.(mp4|webm|ogg)$/i.test(media);
    },
    getMediaPath() {
      try {
        // Check for video first
        const videoPath =
          this.serviceData &&
          this.serviceData.serviceSpecialist &&
          this.serviceData.serviceSpecialist.video;

        if (videoPath) {
          return require(`@/assets/imgs/${videoPath}`);
        }

        // Fall back to image
        const imagePath =
          this.serviceData &&
          this.serviceData.serviceSpecialist &&
          this.serviceData.serviceSpecialist.image;

        if (imagePath) {
          return require(`@/assets/imgs/${imagePath}`);
        }

        // Default fallback
        return require("@/assets/imgs/garageservices2.jpg");
      } catch (error) {
        console.warn("Media not found:", error);
        return require("@/assets/imgs/garageservices2.jpg");
      }
    },
    getImageAlt() {
      const baseAlt =
        (this.serviceData &&
          this.serviceData.serviceSpecialist &&
          this.serviceData.serviceSpecialist.imageAlt) ||
        "Professional garage door service specialist";

      return this.cityName ? `${baseAlt} in ${this.cityName}` : baseAlt;
    },
    getImageTitle() {
      const baseTitle =
        (this.serviceData &&
          this.serviceData.serviceSpecialist &&
          this.serviceData.serviceSpecialist.imageTitle) ||
        "Expert Garage Door Specialists - Certified Technicians Providing Professional Service with Complete Solutions and Reliable Support";

      if (this.cityName) {
        const citySuffix = ` in ${this.cityName}`;
        const maxBaseLength = 155 - citySuffix.length;
        const truncatedBase =
          baseTitle.length > maxBaseLength
            ? baseTitle.substring(0, maxBaseLength).trim() + "..."
            : baseTitle;
        return `${truncatedBase}${citySuffix}`;
      }

      if (baseTitle.length > 160) {
        return baseTitle.substring(0, 157).trim() + "...";
      } else if (baseTitle.length < 150) {
        return baseTitle + " - Local Garage Door Experts You Can Trust";
      }

      return baseTitle;
    },
  },
};
</script>

<style lang="scss" scoped>
.opener-specialists-section {
  position: relative;
  background-repeat: no-repeat;
  background-size: cover;
  background-position: center;
  background-attachment: fixed;
  padding: 80px 0;
  overflow: hidden;

  @media (max-width: 768px) {
    padding: 60px 0;
    background-attachment: scroll;
  }

  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: rgba(0, 0, 0, 0.75);
    z-index: 1;
  }

  &::after {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: linear-gradient(
      135deg,
      transparent 0%,
      transparent 40%,
      rgba(13, 71, 161, 0.2) 70%,
      rgba(13, 71, 161, 0.3) 100%
    );
    z-index: 2;
  }

  .container {
    position: relative;
    z-index: 3;
  }

  .content-wrapper {
    padding-right: 2rem;

    @media (max-width: 991px) {
      padding-right: 0;
      text-align: center;
    }
  }

  .section-title {
    font-size: 2.5rem;
    font-weight: 700;
    line-height: 1.2;
    text-shadow: 2px 2px 4px rgba(0, 0, 0, 0.8);

    @media (max-width: 768px) {
      font-size: 2rem;
    }

    @media (max-width: 576px) {
      font-size: 1.75rem;
    }
  }

  .section-description {
    font-size: 1.1rem;
    line-height: 1.7;
    text-shadow: 1px 1px 2px rgba(0, 0, 0, 0.8);

    p {
      margin-bottom: 1rem;

      &:last-child {
        margin-bottom: 0;
      }
    }

    a {
      color: #0d47a1;
      font-weight: 700;
    }

    @media (max-width: 768px) {
      font-size: 1rem;
    }
  }

  .media-wrapper {
    position: relative;

    &::before {
      content: "";
      position: absolute;
      top: -20px;
      right: -20px;
      width: 100%;
      height: 100%;
      border-radius: 1rem;
      z-index: -1;
      opacity: 0.3;

      @media (max-width: 991px) {
        top: -10px;
        right: -10px;
      }
    }
  }

  .opener-media {
    width: 100%;
    height: auto;
    border-radius: 1rem;
    transition: transform 0.3s ease;
    object-fit: cover;

    &:hover {
      transform: translateY(-8px);
    }
  }

  video.opener-media {
    max-height: 500px;

    @media (max-width: 768px) {
      max-height: 350px;
    }
  }
}
</style>
